import {
  Text,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  IconServer,
  IndigoIndigo50,
  IndigoIndigo60,
  Link,
  Portal,
  Primary12Regular,
  Primary12SemiBold,
  RedRed80,
  Tooltip,
  Box,
  Flex
} from '@sequencehq/core-components'
import { ApiQueryDevtoolsPanel } from '@sequencehq/api'
import { FC, useMemo, useState } from 'react'
import { CacheEntriesModal } from './cacheEntries.modal'
import { MetricGraph } from './MetricGraph'
import {
  QUERY_CACHE_UPPER_LIMIT_BYTES,
  formatBytes,
  formatPercent
} from './performanceMonitorBar.constants'
import { MetricSample } from './performanceMonitorBar.types'
import { useQueryCacheUsage } from './useQueryCacheUsage'
import { usePersistedCacheUsage } from './usePersistedCacheUsage'
import { VerticalGauge } from './VerticalGauge'

type QueryCacheItemProps = {
  isExpanded: boolean
}

const QUERY_CACHE_TOOLTIP_LABEL = 'TanStack query cache'

type CacheModalView = 'in-memory' | 'persisted' | null

export const QueryCacheItem: FC<QueryCacheItemProps> = ({ isExpanded }) => {
  const { stats, entries, history } = useQueryCacheUsage()
  const {
    entries: persistedEntries,
    totalEntries: persistedTotalEntries,
    totalBytes: persistedTotalBytes
  } = usePersistedCacheUsage()
  const { totalEntries, totalBytes } = stats
  const [modalView, setModalView] = useState<CacheModalView>(null)
  const [isDevtoolsOpen, setIsDevtoolsOpen] = useState(false)

  const isOverThreshold = totalBytes > QUERY_CACHE_UPPER_LIMIT_BYTES
  const accentColor = isOverThreshold ? RedRed80 : IndigoIndigo50
  const valueColor = isOverThreshold ? RedRed80 : GreyGrey80
  const iconColor = isOverThreshold ? RedRed80 : GreyGrey80

  const percentUsed = Math.min(
    100,
    (totalBytes / QUERY_CACHE_UPPER_LIMIT_BYTES) * 100
  )

  const metricHistory = useMemo<MetricSample[]>(
    () =>
      history.map(sample => ({
        timestamp: sample.timestamp,
        value: sample.totalBytes
      })),
    [history]
  )

  if (!isExpanded) {
    return (
      <Flex flexDirection="column" alignItems="center" gap="6px" width="100%">
        <Tooltip label={QUERY_CACHE_TOOLTIP_LABEL} placement="right" hasArrow>
          <IconServer color={iconColor} />
        </Tooltip>
        <VerticalGauge
          percentUsed={percentUsed}
          hasValue
          accentColor={accentColor}
          borderColor={GreyGrey30}
          tooltipLabel={`${formatBytes(totalBytes)} across ${totalEntries} entries`}
        />
        <Text {...Primary12SemiBold} color={valueColor}>
          {formatPercent(percentUsed)}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex flexDirection="column" gap="12px" paddingX="12px" width="100%">
      <Flex alignItems="center" gap="6px">
        <Tooltip label={QUERY_CACHE_TOOLTIP_LABEL} placement="right" hasArrow>
          <IconServer size={14} color={iconColor} />
        </Tooltip>
        <Text {...Primary12SemiBold} color={GreyGrey80}>
          TanStack query cache
        </Text>
      </Flex>
      <MetricGraph
        history={metricHistory}
        yMax={QUERY_CACHE_UPPER_LIMIT_BYTES}
        threshold={null}
        accentColor={accentColor}
        borderColor={GreyGrey30}
        isOverThreshold={isOverThreshold}
        formatValue={formatBytes}
      />
      <Flex flexDirection="column" gap="4px">
        <Text {...Primary12Regular} color={GreyGrey60}>
          Entries:{' '}
          <Link
            data-testid="performance-monitor-bar.queryCache.entries"
            {...Primary12Regular}
            color={IndigoIndigo60}
            onClick={() => setModalView('in-memory')}
          >
            {totalEntries}
          </Link>
        </Text>
        <Text {...Primary12Regular} color={GreyGrey60}>
          Size: {formatBytes(totalBytes)} ({formatPercent(percentUsed)})
        </Text>
        <Text {...Primary12Regular} color={GreyGrey60}>
          Limit: {formatBytes(QUERY_CACHE_UPPER_LIMIT_BYTES)}
        </Text>
        <Text {...Primary12Regular} color={GreyGrey60}>
          Persisted (IndexedDB):{' '}
          <Link
            {...Primary12Regular}
            color={IndigoIndigo60}
            onClick={() => setModalView('persisted')}
          >
            {persistedTotalEntries}
          </Link>{' '}
          · {formatBytes(persistedTotalBytes)}
        </Text>
        <Text {...Primary12Regular} color={GreyGrey60}>
          <Link
            {...Primary12Regular}
            color={IndigoIndigo60}
            onClick={() => setIsDevtoolsOpen(prev => !prev)}
          >
            {isDevtoolsOpen
              ? 'Close TanStack devtools'
              : 'Open TanStack devtools'}
          </Link>
        </Text>
      </Flex>
      {isOverThreshold && (
        <Text {...Primary12SemiBold} color={RedRed80}>
          ⚠ Cache above {formatBytes(QUERY_CACHE_UPPER_LIMIT_BYTES)}
        </Text>
      )}
      {modalView === 'in-memory' && (
        <CacheEntriesModal
          title="Query cache entries"
          entries={entries}
          onClose={() => setModalView(null)}
        />
      )}
      {modalView === 'persisted' && (
        <CacheEntriesModal
          title="Persisted query cache (IndexedDB)"
          entries={persistedEntries}
          onClose={() => setModalView(null)}
        />
      )}
      {isDevtoolsOpen && (
        <Portal>
          <Box
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            height="500px"
            zIndex={10}
          >
            <ApiQueryDevtoolsPanel onClose={() => setIsDevtoolsOpen(false)} />
          </Box>
        </Portal>
      )}
    </Flex>
  )
}
